import './TextInput.scss';
import cn from 'classnames';
import { FocusEvent, InputHTMLAttributes, ReactNode, useId, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import { Appearance, Design, Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';
import { useToolbarSize } from '../Toolbar';
import { CONTROL_SIZES } from './text-input-constants';

interface TextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  'size' | 'onChange' | 'onBlur' | 'value' | 'aria-invalid'
> {
  value: string;
  /** Called on every edit, including clear. */
  onChange?: (value: string) => void;
  /**
   * Called with the typed value on blur, not on every edit, and only if it differs from `value`. Clear commits
   * `''` right away. The field then shows `value` again. A `value` change while the field is
   * focused doesn't replace what's being typed.
   */
  onBlurChange?: (value: string) => void;
  /** Called on every blur, before `onBlurChange`. */
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  size?: SizeType;
  label?: string;
  /** A string shows below the field in place of `helper`; either form turns the border red. */
  error?: string | boolean;
  helper?: string;
  /** An icon inside the start of the field. */
  leadingElement?: ReactNode;
  /** An icon inside the end of the field, beside the clear button. */
  trailingElement?: ReactNode;
  multiline?: boolean;
  rows?: number;
  /** Shows a clear button while the field has a value. Hidden when `disabled` or `readOnly`. */
  clearable?: boolean;
}

export const TextInput = ({
  value,
  onChange,
  onBlurChange,
  onBlur,
  size: sizeProp,
  label,
  error,
  helper,
  leadingElement,
  trailingElement,
  multiline = false,
  rows,
  clearable = false,
  disabled,
  readOnly,
  id,
  className,
  'aria-describedby': ariaDescribedBy,
  ...inputPassthroughProps
}: TextInputProps): ReactNode => {
  const size = useToolbarSize(sizeProp, Size.LG);
  const controlRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const autoId = useId();
  const resolvedId = id ?? autoId;
  const messageId = `${resolvedId}-message`;
  // The typed text in onBlurChange mode, from the first edit until blur or clear. Null shows value.
  const [draft, setDraft] = useState<string | null>(null);

  const shownValue = onBlurChange && draft !== null ? draft : value;
  const showClear = clearable && !disabled && !readOnly && shownValue !== '';
  const message = (typeof error === 'string' && error) || helper;
  const handleChange = (next: string) => {
    if (onBlurChange) {
      setDraft(next);
    }
    onChange?.(next);
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur?.(event);
    if (!onBlurChange) return;
    if (draft !== null && draft !== value) {
      onBlurChange(draft);
    }
    setDraft(null);
  };
  const handleClear = () => {
    setDraft(null);
    onChange?.('');
    if (value !== '') {
      onBlurChange?.('');
    }
    // The clear button unmounts once the value is empty, so move focus back to the field.
    controlRef.current?.focus();
  };

  return (
    <div className={cn('text-input', size, { multiline }, className)}>
      {label && <Form.Label htmlFor={resolvedId}>{label}</Form.Label>}
      <div className='text-input-control'>
        {leadingElement && <div className='text-input-leading'>{leadingElement}</div>}
        <Form.Control
          {...inputPassthroughProps}
          ref={controlRef}
          id={resolvedId}
          size={CONTROL_SIZES[size]}
          {...(multiline ? { as: 'textarea' as const, rows } : {})}
          className={cn({
            'has-leading': leadingElement,
            'has-trailing': trailingElement || clearable,
            'has-both': trailingElement && showClear
          })}
          value={shownValue}
          disabled={disabled}
          readOnly={readOnly}
          isInvalid={Boolean(error)}
          aria-invalid={Boolean(error)}
          aria-describedby={cn(ariaDescribedBy, { [messageId]: message }) || undefined}
          onChange={event => handleChange(event.currentTarget.value)}
          onBlur={handleBlur}
        />
        {(trailingElement || showClear) && (
          <div className='text-input-end'>
            {showClear && (
              <IconButton
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                size={Size.SM}
                aria-label='Clear'
                onMouseDown={event => event.preventDefault()}
                onClick={handleClear}
              >
                <MdClose />
              </IconButton>
            )}
            {trailingElement}
          </div>
        )}
      </div>
      {message && (
        <div id={messageId} className={cn('text-input-message', { error })}>
          {message}
        </div>
      )}
    </div>
  );
};
